import React from 'react';

/*
 * Deliberately rounded-md (a rectangle), never rounded-full — that shape
 * difference is what separates an action from a Badge label at a glance.
 * Every clickable action in the portal (page actions, table row actions,
 * modal actions) should route through this component rather than a
 * one-off <button className="..."> so the shape/weight stays consistent.
 */

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger' | 'danger-ghost';
export type ButtonSize = 'sm' | 'md';

const VARIANT_STYLES: Record<ButtonVariant, string> = {
  primary: 'bg-primary-700 text-white border border-primary-700 hover:bg-primary-800 shadow-sm dark:bg-primary-600 dark:border-primary-600 dark:hover:bg-primary-500',
  secondary: 'bg-white text-gray-700 border border-gray-300 hover:bg-gray-50 shadow-sm dark:bg-gray-800 dark:text-gray-200 dark:border-gray-600 dark:hover:bg-gray-700',
  ghost: 'bg-transparent text-gray-600 border border-transparent hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-gray-800',
  danger: 'bg-red-600 text-white border border-red-600 hover:bg-red-700 shadow-sm',
  'danger-ghost': 'bg-transparent text-red-600 border border-transparent hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950',
};

const SIZE_STYLES: Record<ButtonSize, string> = {
  sm: 'px-2.5 py-1.5 text-xs gap-1.5',
  md: 'px-3.5 py-2 text-sm gap-2',
};

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  icon?: React.ComponentType<React.SVGProps<SVGSVGElement>>;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ variant = 'secondary', size = 'md', icon: Icon, className = '', children, ...rest }, ref) => (
    <button
      ref={ref}
      className={`inline-flex items-center justify-center rounded-md font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-1 disabled:cursor-not-allowed disabled:opacity-50 ${VARIANT_STYLES[variant]} ${SIZE_STYLES[size]} ${className}`}
      {...rest}
    >
      {Icon && <Icon className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} />}
      {children}
    </button>
  )
);
Button.displayName = 'Button';

export default Button;
